import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Smartphone, ExternalLink, Loader } from 'lucide-react';

/**
 * Composant de redirection NFC
 * Gère l'ouverture automatique de la PWA sur Android et du site sur iOS
 */
const NFCRedirect = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('Traitement du tag NFC...');

  useEffect(() => {
    const handleNFCRedirect = async () => {
      try {
        // Récupérer les données du tag NFC
        const encodedData = searchParams.get('data');

        if (!encodedData) {
          setStatus('error');
          setMessage('Aucune donnée NFC trouvée');
          return;
        }

        // Décoder les données
        const decodedData = JSON.parse(atob(encodedData));
        const { clientId, cid, exp } = decodedData;

        // Vérifier l'expiration
        const currentDate = new Date();
        const expDate = new Date(exp);

        if (currentDate > expDate) {
          setStatus('error');
          setMessage('Le tag NFC a expiré. Veuillez le reprogrammer.');
          return;
        }

        // Détecter la plateforme
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        const isAndroid = /android/i.test(userAgent);
        const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches
                          || window.navigator.standalone
                          || document.referrer.includes('android-app://');

        console.log('Platform detection:', { isAndroid, isIOS, isStandalone });

        // Si déjà dans la PWA, rediriger directement
        if (isStandalone) {
          setStatus('success');
          setMessage('Ouverture dans l\'application...');

          // Rediriger vers le dashboard ou une page spécifique
          setTimeout(() => {
            navigate('/dashboard', {
              state: {
                nfcScan: true,
                clientId,
                cid
              }
            });
          }, 1000);
          return;
        }

        // Android: Essayer d'ouvrir la PWA installée
        if (isAndroid) {
          setStatus('redirect');
          setMessage('Ouverture de l\'application...');

          // Intent URL pour ouvrir la PWA installée
          const intentUrl = `intent://agenda.omadservices.com/dashboard#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=https://agenda.omadservices.com;end`;

          // Essayer d'ouvrir avec l'intent
          window.location.href = intentUrl;

          // Fallback: Si la PWA n'est pas installée, rediriger vers le site après 2 secondes
          setTimeout(() => {
            if (!document.hidden) {
              setStatus('fallback');
              setMessage('Redirection vers le site web...');
              window.location.href = 'https://agenda.omadservices.com/dashboard';
            }
          }, 2000);
        }

        // iOS: Rediriger vers le site (PWA s'ouvrira si installée)
        else if (isIOS) {
          setStatus('redirect');
          setMessage('Ouverture du site...');

          // Sur iOS, on redirige simplement vers le site
          // Si la PWA est installée, elle s'ouvrira automatiquement
          setTimeout(() => {
            window.location.href = 'https://agenda.omadservices.com/dashboard';
          }, 1000);
        }

        // Autres plateformes (Desktop, etc.)
        else {
          setStatus('redirect');
          setMessage('Redirection vers le site...');
          setTimeout(() => {
            window.location.href = 'https://agenda.omadservices.com/dashboard';
          }, 1000);
        }

      } catch (error) {
        console.error('Erreur lors du traitement du tag NFC:', error);
        setStatus('error');
        setMessage('Erreur lors de la lecture du tag NFC');
      }
    };

    handleNFCRedirect();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full">
        {/* Icône de statut */}
        <div className="flex justify-center mb-6">
          {status === 'loading' && (
            <div className="bg-blue-100 dark:bg-blue-900 p-4 rounded-full">
              <Loader className="w-12 h-12 text-blue-600 dark:text-blue-400 animate-spin" />
            </div>
          )}
          {status === 'redirect' && (
            <div className="bg-green-100 dark:bg-green-900 p-4 rounded-full">
              <Smartphone className="w-12 h-12 text-green-600 dark:text-green-400" />
            </div>
          )}
          {status === 'success' && (
            <div className="bg-green-100 dark:bg-green-900 p-4 rounded-full">
              <ExternalLink className="w-12 h-12 text-green-600 dark:text-green-400" />
            </div>
          )}
          {status === 'error' && (
            <div className="bg-red-100 dark:bg-red-900 p-4 rounded-full">
              <ExternalLink className="w-12 h-12 text-red-600 dark:text-red-400" />
            </div>
          )}
        </div>

        {/* Titre */}
        <h1 className="text-2xl font-bold text-center mb-4 text-gray-900 dark:text-white">
          {status === 'loading' && 'Lecture du tag NFC'}
          {status === 'redirect' && 'Redirection'}
          {status === 'success' && 'Succès'}
          {status === 'error' && 'Erreur'}
        </h1>

        {/* Message */}
        <p className="text-center text-gray-600 dark:text-gray-300 mb-6">
          {message}
        </p>

        {/* Barre de progression */}
        {(status === 'loading' || status === 'redirect') && (
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
            <div className="bg-blue-600 dark:bg-blue-400 h-full rounded-full animate-pulse" style={{ width: '70%' }}></div>
          </div>
        )}

        {/* Bouton de secours en cas d'erreur */}
        {status === 'error' && (
          <button
            onClick={() => window.location.href = 'https://agenda.omadservices.com'}
            className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
          >
            Ouvrir manuellement
          </button>
        )}

        {/* Instructions */}
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
          <p className="text-sm text-blue-800 dark:text-blue-200 text-center">
            {status === 'redirect' && 'Si la redirection échoue, veuillez ouvrir l\'application manuellement.'}
            {status === 'loading' && 'Veuillez patienter pendant le traitement...'}
            {status === 'success' && 'Vous allez être redirigé automatiquement.'}
            {status === 'error' && 'Veuillez réessayer ou contacter le support.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default NFCRedirect;
